/**
 * デモガイダンスコンポーネント
 * デモモードでの操作案内とシナリオ説明を提供
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  InformationCircleIcon,
  PlayCircleIcon,
  CheckCircleIcon,
  LightBulbIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui';
import { isDemoMode } from '../../utils/environment';
import { demoDataService, DemoScenario } from '../../services/demoDataService';
import { useAuth } from '../../hooks/useAuth';
import { Role } from '../../types';

interface DemoGuidanceProps {
  onClose?: () => void;
  onStartScenario?: (scenarioId: string) => void;
}

/**
 * デモガイダンスメインコンポーネント
 */
export const DemoGuidance: React.FC<DemoGuidanceProps> = ({ onClose, onStartScenario }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [scenarios, setScenarios] = useState<DemoScenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [hasShownGuidance, setHasShownGuidance] = useState(false);

  useEffect(() => {
    if (!isDemoMode()) return;

    // デモガイダンス表示が必要かチェック
    const shouldShow = demoDataService.shouldShowGuidance();
    if (shouldShow && !hasShownGuidance) {
      setIsVisible(true);
      setHasShownGuidance(true);
    }

    // 利用可能なシナリオを取得
    const availableScenarios = demoDataService.getAvailableScenarios();
    setScenarios(availableScenarios);

    if (availableScenarios.length > 0) {
      setSelectedScenario(availableScenarios[0].id);
    }
  }, [hasShownGuidance]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    onClose?.();
  }, [onClose]);

  const handleStartScenario = useCallback(
    (scenarioId: string) => {
      onStartScenario?.(scenarioId);
      handleClose();
    },
    [onStartScenario, handleClose]
  );

  if (!isDemoMode() || !isVisible) {
    return null;
  }

  const selectedScenarioData = scenarios.find(s => s.id === selectedScenario);

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex items-center justify-center min-h-full p-4 text-center sm:p-0'>
        {/* オーバーレイ */}
        <div className='fixed inset-0 bg-gray-900/50 transition-opacity' onClick={handleClose} />

        {/* モーダル */}
        <div className='relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6'>
          {/* ヘッダー */}
          <div className='flex items-start justify-between'>
            <div className='flex items-center'>
              <div className='mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50 sm:mx-0'>
                <InformationCircleIcon className='h-6 w-6 text-blue-600 dark:text-blue-400' />
              </div>
              <div className='ml-4'>
                <h3 className='text-lg font-medium leading-6 text-gray-900 dark:text-white'>
                  ラクハイデモ環境へようこそ！
                </h3>
                <p className='text-sm text-gray-500 dark:text-gray-400'>
                  陸送管理システムの機能を体験してみましょう
                </p>
              </div>
            </div>
            <button
              type='button'
              className='rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none'
              onClick={handleClose}
            >
              <XMarkIcon className='h-6 w-6' />
            </button>
          </div>

          {/* コンテンツ */}
          <div className='mt-6'>
            {/* デモ環境の説明 */}
            <div className='mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg'>
              <div className='flex items-start'>
                <LightBulbIcon className='h-5 w-5 text-blue-500 mt-0.5 mr-3' />
                <div>
                  <h4 className='text-sm font-medium text-blue-900 dark:text-blue-200'>
                    デモ環境について
                  </h4>
                  <ul className='mt-2 text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1'>
                    <li>実際のデータに影響はありません</li>
                    <li>すべての機能を安全に試すことができます</li>
                    <li>「データリセット」で初期状態に戻せます</li>
                    <li>リアルな業務フローを体験できます</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* シナリオ選択 */}
            <div className='mb-6'>
              <h4 className='text-base font-medium text-gray-900 dark:text-white mb-4'>
                体験シナリオを選択してください
              </h4>
              <div className='space-y-3'>
                {scenarios.map(scenario => (
                  <div
                    key={scenario.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedScenario === scenario.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                    onClick={() => setSelectedScenario(scenario.id)}
                  >
                    <div className='flex items-start'>
                      <div className='flex-shrink-0'>
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            selectedScenario === scenario.id
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300 dark:border-gray-500'
                          }`}
                        >
                          {selectedScenario === scenario.id && (
                            <div className='w-2 h-2 rounded-full bg-white' />
                          )}
                        </div>
                      </div>
                      <div className='ml-3'>
                        <h5 className='text-sm font-medium text-gray-900 dark:text-white'>
                          {scenario.name}
                        </h5>
                        <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                          {scenario.description}
                        </p>
                        <div className='flex items-center mt-2 text-xs text-gray-400 dark:text-gray-500'>
                          <span>所要時間: 約{scenario.estimatedDuration}分</span>
                          <span className='mx-2'>•</span>
                          <span>{scenario.steps.length}ステップ</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 選択されたシナリオの詳細 */}
            {selectedScenarioData && (
              <div className='mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg'>
                <h5 className='text-sm font-medium text-gray-900 dark:text-white mb-3'>
                  「{selectedScenarioData.name}」の手順
                </h5>
                <div className='space-y-2'>
                  {selectedScenarioData.steps.map((step, index) => (
                    <div key={step.id} className='flex items-start text-sm'>
                      <span className='flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xs font-medium mr-3'>
                        {index + 1}
                      </span>
                      <div>
                        <span className='text-gray-900 dark:text-white font-medium'>
                          {step.title}
                        </span>
                        <p className='text-gray-500 dark:text-gray-400 mt-0.5'>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* アクションボタン */}
          <div className='mt-6 flex flex-col sm:flex-row gap-3'>
            <Button
              onClick={() => selectedScenario && handleStartScenario(selectedScenario)}
              disabled={!selectedScenario}
              className='flex-1 flex items-center justify-center'
            >
              <PlayCircleIcon className='h-5 w-5 mr-2' />
              シナリオを開始
            </Button>
            <Button variant='ghost' onClick={handleClose} className='flex-1'>
              後で確認する
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * デモガイダンストリガーボタン（常時表示用）
 */
export const DemoGuidanceTrigger: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  if (!isDemoMode()) return null;

  return (
    <button
      onClick={onClick}
      className='fixed bottom-4 right-4 z-40 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-colors'
      aria-label='デモガイダンスを表示'
    >
      <InformationCircleIcon className='h-6 w-6' />
    </button>
  );
};

/**
 * デモステータスバー（上部表示用）
 */
export const DemoStatusBar: React.FC<{
  onResetDemo?: () => void;
}> = ({ onResetDemo }) => {
  const [demoStatus, setDemoStatus] = useState(demoDataService.getDemoStatus());
  const { user } = useAuth();

  useEffect(() => {
    const interval = setInterval(() => {
      setDemoStatus(demoDataService.getDemoStatus());
    }, 30000); // 30秒ごとに更新

    return () => clearInterval(interval);
  }, []);

  if (!isDemoMode()) return null;

  // 管理者のみリセットボタンを表示
  const canReset = user?.role === Role.ADMIN;

  return (
    <div className='bg-blue-600 text-white px-4 py-2 text-sm flex items-center justify-between'>
      <div className='flex items-center'>
        <div className='w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse' />
        <span className='font-medium'>デモ環境</span>
        <span className='mx-2'>|</span>
        <span>安全に機能をお試しいただけます</span>
      </div>
      <div className='flex items-center gap-4 text-xs'>
        {demoStatus.lastResetAt && (
          <div className='flex items-center'>
            <CheckCircleIcon className='h-4 w-4 mr-1' />
            <span>
              最終リセット:{' '}
              {demoStatus.lastResetAt.toLocaleTimeString('ja-JP', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        )}
        {canReset && onResetDemo && (
          <button
            onClick={onResetDemo}
            className='flex items-center gap-1 px-3 py-1 bg-blue-700 hover:bg-blue-800 rounded text-white font-medium transition-colors'
            title='デモデータを初期状態に戻します'
          >
            <ArrowPathIcon className='h-3 w-3' />
            データを初期化
          </button>
        )}
      </div>
    </div>
  );
};
